import { useMemo, useState } from 'react';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';

import type { ClientPortalTimeRange } from '../common/constants';
import type { AccountPeriodReturns } from './account-performance';
import type { PortfolioHistoryGroupInput } from './net-worth';
import { CLIENT_PORTAL_HEADLINE_NUMBER_RESPONSIVE_CLASS } from '../common/constants';
import { InfoBubble } from '../common/info-bubble';
import { PortalCard, PortalCardFooter } from '../common/portal-section';
import { TimeRangeToggle } from '../common/time-range-toggle';
import { resolveTimeRange } from '../common/time-ranges';
import { TransferFooterNote } from '../common/transfer-footer-note';
import { safeMoney } from '../common/utils';
import { ValueChange } from '../common/value-change';
import { accountChangeCaption, accountReturnNote } from './account-detail';
import { accountDetailTimeRanges } from './account-performance';
import { BalanceHistoryChartSlot } from './balance-history-chart';
import {
  ACCOUNT_RETURN_PERIOD_CAPTIONS,
  ACCOUNT_RETURN_TOOLTIP_LABEL,
  DEFAULT_NET_WORTH_RANGE,
  NET_WORTH_CHART_EMPTY_DESCRIPTION,
  NET_WORTH_CHART_EMPTY_TITLE,
  NET_WORTH_CHART_GRADIENT_ID,
  NET_WORTH_CHART_LABEL,
  NET_WORTH_CHART_PENDING_DELTA_LABEL,
  NET_WORTH_CHART_UNAVAILABLE_DELTA_LABEL,
  NET_WORTH_FOOTER_ROW_HEIGHT_CLASS,
  NET_WORTH_FOOTER_TEST_ID,
  NET_WORTH_LABEL,
  NET_WORTH_PERIOD_CAPTIONS,
  NET_WORTH_RANGE_CONTROL_LABEL,
  NET_WORTH_RETURN_TOOLTIP_NOTE,
  NET_WORTH_TWO_BASES_CAPTION_AWAITING_COMPLIANCE,
  NET_WORTH_TWO_BASES_TOOLTIP_LABEL,
} from './constants';
import { buildNetWorthSeries, netWorthChange, sliceNetWorthSeries } from './net-worth';

const NO_RETURNS: AccountPeriodReturns = {};

type AccountsNetWorthCardProps = {
  readonly netWorth: number;
  readonly historyGroups: readonly PortfolioHistoryGroupInput[] | undefined;
  readonly historyLoading: boolean;
  readonly historyFailed: boolean;
  readonly onRetryHistory: () => void;
  readonly balancesUpdatedLabel: string | null;
  readonly transferring: boolean;
  readonly destinationCustodian: string | null;
  readonly returns?: AccountPeriodReturns;
  readonly returnsLoading?: boolean;
};

export function AccountsNetWorthCard({
  netWorth,
  historyGroups,
  historyLoading,
  historyFailed,
  onRetryHistory,
  balancesUpdatedLabel,
  transferring,
  destinationCustodian,
  returns = NO_RETURNS,
  returnsLoading = false,
}: AccountsNetWorthCardProps) {
  const [preferredTimeRange, setPreferredTimeRange] = useState<ClientPortalTimeRange>(DEFAULT_NET_WORTH_RANGE);
  const series = useMemo(() => buildNetWorthSeries(historyGroups ?? []), [historyGroups]);
  const timeRanges = accountDetailTimeRanges(series, returns);
  const timeRange = resolveTimeRange(preferredTimeRange, timeRanges);
  const points = useMemo(() => sliceNetWorthSeries(series, timeRange), [series, timeRange]);
  const periodReturn = returns[timeRange];
  const change = isNil(periodReturn) ? netWorthChange(points) : periodReturn;
  const caption = isNil(periodReturn)
    ? accountChangeCaption(NET_WORTH_PERIOD_CAPTIONS[timeRange], points.at(-1)?.date)
    : ACCOUNT_RETURN_PERIOD_CAPTIONS[timeRange];
  const captionNote = isNil(periodReturn) ? undefined : (
    <InfoBubble label={ACCOUNT_RETURN_TOOLTIP_LABEL}>
      {accountReturnNote(periodReturn.endDate, NET_WORTH_RETURN_TOOLTIP_NOTE)}
    </InfoBubble>
  );
  const changeLoading = historyLoading || returnsLoading;
  const changeFailed = historyFailed && isNil(periodReturn);

  return (
    <PortalCard className="overflow-hidden p-0">
      <div className="flex flex-col gap-2 px-6 pt-6">
        <Text variant="body-sm">{NET_WORTH_LABEL}</Text>
        <p
          className={cn(CLIENT_PORTAL_HEADLINE_NUMBER_RESPONSIVE_CLASS, 'text-dark')}
          data-testid="client-portal-net-worth-amount"
        >
          {safeMoney(netWorth)}
        </p>
        {changeLoading && <Skeleton className="h-5 w-48" />}
        {!changeLoading && (changeFailed || isNil(change)) && (
          <Text variant="body-sm" data-testid="client-portal-net-worth-change">
            {changeFailed ? NET_WORTH_CHART_UNAVAILABLE_DELTA_LABEL : NET_WORTH_CHART_PENDING_DELTA_LABEL}
          </Text>
        )}
        {!changeLoading && !changeFailed && !isNil(change) && (
          <span data-testid="client-portal-net-worth-change">
            <ValueChange amount={change.amount} percent={change.percent} caption={caption} captionNote={captionNote} />
          </span>
        )}
      </div>

      <div className="flex flex-col items-center gap-4 px-2 pb-6 pt-4">
        <div className="w-full">
          <BalanceHistoryChartSlot
            loading={historyLoading}
            failed={historyFailed}
            points={points}
            onRetry={onRetryHistory}
            seriesLabel={NET_WORTH_LABEL}
            chartLabel={NET_WORTH_CHART_LABEL}
            gradientId={NET_WORTH_CHART_GRADIENT_ID}
            emptyTitle={NET_WORTH_CHART_EMPTY_TITLE}
            emptyDescription={NET_WORTH_CHART_EMPTY_DESCRIPTION}
            testIdPrefix="client-portal-net-worth"
          />
        </div>
        <TimeRangeToggle
          value={timeRange}
          onChange={setPreferredTimeRange}
          ranges={timeRanges}
          label={NET_WORTH_RANGE_CONTROL_LABEL}
        />
      </div>

      <PortalCardFooter className="bg-background-light">
        <div
          className={cn(
            'flex items-center justify-center gap-1.5 max-md:h-auto max-md:flex-wrap max-md:py-1 max-md:text-center',
            NET_WORTH_FOOTER_ROW_HEIGHT_CLASS,
          )}
          data-testid={NET_WORTH_FOOTER_TEST_ID}
        >
          {transferring ? (
            <TransferFooterNote custodian={destinationCustodian} />
          ) : (
            <>
              {!isNil(balancesUpdatedLabel) && (
                <Text variant="caption" color="secondary">
                  {balancesUpdatedLabel}
                </Text>
              )}
              <InfoBubble label={NET_WORTH_TWO_BASES_TOOLTIP_LABEL}>
                {NET_WORTH_TWO_BASES_CAPTION_AWAITING_COMPLIANCE}
              </InfoBubble>
            </>
          )}
        </div>
      </PortalCardFooter>
    </PortalCard>
  );
}
